import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Calendar, Clock, ChevronRight, Share2, Play } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ShareMenu } from '../components/ShareMenu';
import usePageTitle from '../hooks/usePageTitle';
import { enhanceAccessibleHtml } from '../utils/a11yHtml';
import JotformEmbedContent from '../components/JotformEmbedContent';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/** Extract YouTube video ID from various URL formats */
function getYoutubeVideoId(url) {
  if (!url || typeof url !== 'string') return null;
  const trimmed = url.trim();
  const patterns = [
    /(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/,
    /(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,
  ];
  for (const re of patterns) {
    const m = trimmed.match(re);
    if (m) return m[1];
  }
  return null;
}

function getPostThumbnailUrl(post) {
  if (post.featured_image_url) {
    return post.featured_image_url.startsWith('/') ? `${BACKEND_URL}${post.featured_image_url}` : post.featured_image_url;
  }
  const videoId = getYoutubeVideoId(post.youtube_url);
  if (videoId) return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  return null;
}

const RelatedPostCard = ({ post }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  return (
    <Link
      to={`/insights/${post.slug}`}
      className="group block"
      data-testid={`related-post-${post.slug}`}
    >
      <article className="bg-white rounded-xl overflow-hidden border border-gray-100 hover:border-[#C8473A]/20 hover:shadow-lg transition-all duration-300">
        <div className="relative aspect-[16/9] overflow-hidden bg-gray-100">
          <img
            src={getPostThumbnailUrl(post) || 'https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=400'}
            alt={post.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="outline" className="text-xs">{post.category}</Badge>
            <span className="text-xs text-muted-light">{formatDate(post.published_at)}</span>
          </div>
          <h4 className="font-semibold text-[#211F1F] group-hover:text-[#C8473A] transition-colors line-clamp-2">
            {post.title}
          </h4>
        </div>
      </article>
    </Link>
  );
};

/** YouTube video: shows thumbnail; click replaces with inline embed */
const YouTubeBlock = ({ videoId, thumbnailUrl, embedUrl, title }) => {
  const [expanded, setExpanded] = useState(false);
  const [thumbError, setThumbError] = useState(false);
  const fallbackThumb = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.25 }}
      className="mb-10"
    >
      {expanded ? (
        <div className="relative w-full rounded-2xl overflow-hidden bg-black aspect-video">
          <iframe
            src={embedUrl}
            title={title || 'YouTube video'}
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="relative w-full rounded-2xl overflow-hidden bg-gray-900 aspect-video group block text-left"
          aria-label={`Play video: ${title || 'YouTube video'}`}
        >
          <img
            src={thumbError ? fallbackThumb : thumbnailUrl}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={() => setThumbError(true)}
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-[#C8473A] flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
              <Play className="w-8 h-8 md:w-10 md:h-10 text-white fill-white ml-1" />
            </div>
          </div>
        </button>
      )}
    </motion.div>
  );
};

const InsightDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  usePageTitle(post?.title || (loading ? 'Insights' : 'Article'));
  const accessibleContent = useMemo(
    () => enhanceAccessibleHtml(post?.content || '', { pageTitle: post?.title }),
    [post?.content, post?.title],
  );

  const fetchPost = useCallback(async () => {
    if (!slug) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/blog/public/slug/${slug}`);
      setPost(res.data);
    } catch (error) {
      console.error('Failed to fetch post:', error);
      if (error.response?.status === 404) {
        navigate('/insights');
        toast.error('Article not found');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, slug]);

  useEffect(() => {
    fetchPost();
  }, [fetchPost]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      weekday: 'long',
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const getReadTime = (content) => {
    if (!content) return '5 min read';
    const words = content.replace(/<[^>]*>/g, '').split(/\s+/).length;
    const mins = Math.ceil(words / 200);
    return `${mins} min read`;
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header />
        <main id="main-content" tabIndex={-1} className="pt-32 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-32 mb-6"></div>
              <div className="h-12 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="h-6 bg-gray-100 rounded w-1/2 mb-8"></div>
              <div className="aspect-[2/1] bg-gray-200 rounded-2xl mb-8"></div>
              <div className="space-y-4">
                <div className="h-4 bg-gray-100 rounded w-full"></div>
                <div className="h-4 bg-gray-100 rounded w-full"></div>
                <div className="h-4 bg-gray-100 rounded w-3/4"></div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      
      <main id="main-content" tabIndex={-1} className="pt-32 pb-16">
        <article className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
            <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <nav className="flex items-center gap-2 text-sm text-gray-500" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-[#C8473A] transition-colors">Home</Link>
              <ChevronRight className="w-4 h-4" aria-hidden="true" />
              <Link to="/insights" className="hover:text-[#C8473A] transition-colors">Insights</Link>
              <ChevronRight className="w-4 h-4" aria-hidden="true" />
              <span className="text-gray-900 truncate max-w-[200px]" aria-current="page">{post.title}</span>
            </nav>
          </motion.div>

          {/* Back Button */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <Link 
              to="/insights"
              className="inline-flex items-center gap-2 text-gray-500 hover:text-[#C8473A] transition-colors"
              data-testid="back-to-insights"
              aria-label="Back to Insights"
            >
              <ArrowLeft className="w-4 h-4" aria-hidden="true" />
              Back to Insights
            </Link>
          </motion.div>

          {/* Header */}
          <motion.header
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8"
          >
            <div className="flex items-center gap-3 mb-4">
              <Badge variant="outline" className="text-sm">
                {post.category || 'General'}
              </Badge>
              {post.is_featured && (
                <Badge className="bg-[#C8473A] text-white">Featured</Badge>
              )}
            </div>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#211F1F] mb-6 leading-tight">
              {post.title}
            </h1>

            {post.excerpt && (
              <p className="text-xl text-gray-600 mb-6 leading-relaxed">
                {post.excerpt}
              </p>
            )}

            {/* Authors */}
            {post.authors?.length > 0 && (
              <div className="flex items-center gap-3 mb-4">
                <span className="text-sm text-gray-500">By</span>
                <div className="flex flex-wrap items-center gap-3">
                  {post.authors.map((author, idx) => (
                    <Link 
                      key={author.member_id}
                      to={`/team/${author.slug || author.member_id}`}
                      className="flex items-center gap-2 hover:bg-gray-50 rounded-lg p-1 -m-1 transition-colors group"
                    >
                      {author.photo_url ? (
                        <img 
                          src={author.photo_url.startsWith('/') ? `${BACKEND_URL}${author.photo_url}` : author.photo_url}
                          alt={author.name}
                          className="w-8 h-8 rounded-full object-cover"
                          onError={(e) => {
                            e.target.src = `https://ui-avatars.com/api/?name=${author.name}&background=C8473A&color=fff&size=32`;
                          }}
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-[#C8473A]/10 flex items-center justify-center">
                          <span className="text-xs font-medium text-yn-brand-on-tint">{author.name?.charAt(0)}</span>
                        </div>
                      )}
                      <span className="text-sm font-medium text-[#211F1F] group-hover:text-[#C8473A] transition-colors">{author.name}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 pb-6 border-b border-gray-200">
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                {formatDate(post.published_at)}
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                {getReadTime(post.content)}
              </span>
            </div>
          </motion.header>

          {/* Featured Image */}
          {post.featured_image_url && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mb-10"
            >
              <img
                src={post.featured_image_url.startsWith('/') ? `${BACKEND_URL}${post.featured_image_url}` : post.featured_image_url}
                alt={post.featured_image_alt || post.title}
                className="w-full rounded-2xl object-cover max-h-[500px]"
              />
            </motion.div>
          )}

          {/* YouTube Video: thumbnail, click to open inline embed */}
          {(() => {
            const videoId = getYoutubeVideoId(post.youtube_url);
            if (!videoId) return null;
            const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
            const embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1`;
            return (
              <YouTubeBlock
                videoId={videoId}
                thumbnailUrl={thumbnailUrl}
                embedUrl={embedUrl}
                title={post.title}
              />
            );
          })()}

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-12"
            data-testid="article-content"
          >
            <JotformEmbedContent
              html={accessibleContent}
              className="prose prose-lg max-w-none
                prose-headings:text-[#211F1F] prose-headings:font-bold prose-headings:leading-tight
                prose-h1:text-3xl prose-h1:mt-8 prose-h1:mb-4
                prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-3
                prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
                prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-4
                prose-a:text-[#9b352c] hover:prose-a:text-[#7a2a1f] hover:prose-a:underline
                prose-strong:text-[#211F1F] prose-strong:font-semibold
                prose-em:italic
                prose-ul:my-4 prose-ul:pl-6 prose-ul:list-disc
                prose-ol:my-4 prose-ol:pl-6 prose-ol:list-decimal
                prose-li:text-gray-700 prose-li:mb-2 prose-li:leading-relaxed
                prose-blockquote:border-l-4 prose-blockquote:border-[#C8473A] prose-blockquote:bg-gray-50 prose-blockquote:py-4 prose-blockquote:px-6 prose-blockquote:my-6 prose-blockquote:italic prose-blockquote:text-gray-600 prose-blockquote:rounded-r-lg prose-blockquote:not-italic
                prose-code:bg-gray-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:font-mono prose-code:text-[#9b352c] prose-code:before:content-none prose-code:after:content-none
                prose-pre:bg-[#1e1e1e] prose-pre:text-gray-100 prose-pre:rounded-xl prose-pre:p-4 prose-pre:my-6 prose-pre:overflow-x-auto
                prose-img:rounded-xl prose-img:shadow-md prose-img:my-6
                prose-hr:my-8 prose-hr:border-gray-200
              "
            />
          </motion.div>

          {/* Tags */}
          {post.tags?.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mb-10 pb-10 border-b border-gray-200"
            >
              <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Tags</h4>
              <div className="flex flex-wrap gap-2">
                {post.tags.map((tag, idx) => (
                  <Link
                    key={idx}
                    to={`/insights?tag=${encodeURIComponent(tag)}`}
                    className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full text-sm transition-colors"
                  >
                    {tag}
                  </Link>
                ))}
              </div>
            </motion.div>
          )}

          {/* Share */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="flex items-center gap-3 mb-12 pb-10 border-b border-gray-200"
          >
            <span className="text-sm font-medium text-gray-700">Share this article</span>
            <ShareMenu
              url={typeof window !== 'undefined' ? window.location.href : ''}
              title={post?.title || ''}
              description={post?.excerpt || ''}
              trigger={
                <Button variant="outline" size="sm" className="gap-2">
                  <Share2 className="w-4 h-4" />
                  Share
                </Button>
              }
            />
          </motion.div>

          {/* Related Posts */}
          {post.related_posts?.length > 0 && (
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <h3 className="text-2xl font-bold text-[#211F1F] mb-6">Related Posts</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {post.related_posts.map(relatedPost => (
                  <RelatedPostCard key={relatedPost.post_id} post={relatedPost} />
                ))}
              </div>
            </motion.section>
          )}
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default InsightDetailPage;
